"use client";

import { SocialLinks, VenueWithLocations } from "../../data/venue_pages";

import ConnectedSocialLinks from "./dashboard/venue-manager/settings/ConnectedSocialLinks";
import { ShowWithComedian } from "@punchup/data/legacy";
import { User } from "@supabase/supabase-js";
import VenueDescription from "./VenueDescription";
import VenueHours from "./VenueHours";
import { VenueInfoTagsThemed } from "./VenueInfoTagsThemed";
import VenueSubscriptionBannerThemed from "./VenueSubscriptionBannerThemed";
import VenueUpcomingShows from "./VenuesUpcomingShows";
import type { VenuePageUrlBase } from "./navigation/utils";
import type { VenueCarouselTheme } from "./VenueCarousel";
import dynamic from "next/dynamic";

// Lazy load below-the-fold components to improve initial page load
const VenueMapCard = dynamic(() => import("./VenueMapCard"), {
  ssr: true,
  loading: () => (
    <div className="w-full h-[200px] bg-neutral-100 animate-pulse rounded-lg" />
  ),
});

const VenueVideo = dynamic(
  () => import("./VenueVideo").then((mod) => ({ default: mod.VenueVideo })),
  {
    ssr: true,
    loading: () => (
      <div className="w-full aspect-video bg-neutral-100 animate-pulse rounded-lg" />
    ),
  }
);

export interface VenuePageProps {
  venue: VenueWithLocations;
  venueSlug: string;
  urlBase: VenuePageUrlBase;
  userEmail?: string;
  isUserSubscribed: boolean;
  hasSocialLinks: boolean;
  socialLinks: SocialLinks;
  VenueCarouselComponent: React.ComponentType<{
    venuePageSlug: string;
    venueLogoImg: string | null;
    shows: ShowWithComedian[];
    theme?: VenueCarouselTheme;
  }>;
  next5Shows: ShowWithComedian[];
  followedShows: ShowWithComedian[];
  followedComedians: string[];
  user?: User;
  internalReferrer?: string | null;
  followedShowsEnabled?: boolean;
}

export default function VenuePage({
  venue,
  venueSlug,
  urlBase,
  userEmail,
  isUserSubscribed,
  hasSocialLinks,
  socialLinks,
  VenueCarouselComponent,
  next5Shows,
  followedShows,
  followedComedians,
  user,
  internalReferrer,
  followedShowsEnabled = false,
}: VenuePageProps) {
  const { is_producer, is_location_hidden, locations } = venue;
  const shouldShowMapCard =
    !is_location_hidden && !is_producer && locations && locations.length > 0;

  return (
    <div className="flex flex-col">
      {/* NOTE: this may be hidden by a feature flag, checked on the client inside the component */}
      <VenueCarouselComponent
        shows={next5Shows}
        venuePageSlug={venueSlug}
        venueLogoImg={venue.logo_img}
      />
      <div className="flex flex-col gap-y-8 md:gap-y-16 lg:px-20 pt-4 md:pt-4">
        <section>
          <div className="px-4 md:px-10">
            <VenueUpcomingShows
              useVenueTheme
              venueSlug={venue.slug}
              urlBase={urlBase}
              followedShows={followedShows}
              followedComedians={followedComedians}
              user={user}
              internalReferrer={internalReferrer}
              followedShowsEnabled={followedShowsEnabled}
            />
          </div>
        </section>

        <section className="px-4 md:px-10 gap-y-8 flex flex-col md:grid md:grid-cols-[1fr_384px] md:gap-x-10">
          <div className="flex flex-col w-full gap-y-8">
            {venue.description && <VenueDescription venuePage={venue} />}
            {venue.info_tags.length > 0 && (
              <>
                <div className="block w-full h-px border-t border-neutral-200" />
                <VenueInfoTagsThemed venuePage={venue} />
                {/* always display a divider beneath the info tags, even if the video is missing */}
                <div className="block w-full h-px border-t border-neutral-200" />
              </>
            )}
            {venue.video_id && (
              <>
                <VenueVideo videoId={venue.video_id} venueId={venue.id} />
              </>
            )}
          </div>
          <div className="flex flex-col gap-y-6">
            {venue.hours && <VenueHours venuePage={venue} />}
            {/* Social Links Section */}
            {hasSocialLinks && (
              <div>
                <ConnectedSocialLinks
                  venueId={venue.id}
                  socialLinks={socialLinks}
                  iconSize={24}
                  publicDisplay={true}
                  displayMode="grayscale"
                  cardClassName="bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  cardSize={48}
                  cardPadding={8}
                  title="Follow us"
                />
              </div>
            )}
          </div>
        </section>

        <section
          className={`px-4 md:px-10 grid grid-cols-1 gap-8 max-w-6xl mx-auto ${
            shouldShowMapCard ? "md:grid-cols-2" : ""
          }`}
        >
          <VenueSubscriptionBannerThemed
            venue={venue}
            userEmail={userEmail}
            isUserSubscribed={isUserSubscribed}
          />
          {shouldShowMapCard && <VenueMapCard venue={venue} title="Location" />}
        </section>
      </div>
    </div>
  );
}
